import { useClientsQuery } from "@/query/clientQuery"
import { useBookingQuery } from "@/query/bookingQuery"
import type { BookingTable, ClientInfoBeauty } from "@okeys/shared-utils"
import { useMemo, useState } from "react"
import {
  Client,
  SortField,
  SortState,
  sortClients,
} from "../../components/Client/utils/clientsUtils"

/**
 * Fonction qui transforme les données du backend en données utilisables pour l'affichage
 * @param clients - Liste des clients du backend
 * @param reservations - Liste des réservations du backend
 * @returns Liste des clients formatés pour l'affichage
 */
const transformBackendDataToClients = (
  clients: ClientInfoBeauty[],
  reservations: BookingTable[]
): Client[] => {
  return clients.map((client) => {
    // Filtrer les réservations de ce client
    const clientReservations = reservations.filter(
      (reservation) => (reservation.clientId || "") === client.clientId
    )

    // Calculer les statistiques du client
    const visitCount = clientReservations.length
    const totalSpent = clientReservations.reduce(
      (sum, reservation) => sum + ((reservation as any).total || (reservation as any).price || 0),
      0
    )

    // Trouver la dernière visite
    const lastVisit =
      clientReservations.length > 0
        ? clientReservations
            .sort(
              (a, b) =>
                new Date(((b as any).endTime || b.scheduledEnd) as string).getTime() -
                new Date(((a as any).endTime || a.scheduledEnd) as string).getTime()
            )[0]
            .scheduledEnd.split("T")[0]
        : new Date().toISOString().split("T")[0]

    // Déterminer le type de client
    const registrationDate =
      clientReservations.length > 0
        ? clientReservations
            .sort(
              (a, b) =>
                new Date(((a as any).startTime || a.scheduledStart) as string).getTime() -
                new Date(((b as any).startTime || b.scheduledStart) as string).getTime()
            )[0]
            .scheduledStart.split("T")[0]
        : new Date().toISOString().split("T")[0]

    const daysSinceRegistration = Math.floor(
      (new Date().getTime() - new Date(registrationDate).getTime()) /
        (1000 * 60 * 60 * 24)
    )

    let type: Client["type"] = "new"
    if (visitCount >= 5) {
      type = "regular"
    } else if (visitCount > 1 && daysSinceRegistration > 30) {
      type = "returning"
    }

    // Extraire prénom et nom du champ name
    const nameParts = client.name.split(" ")
    const firstName = nameParts[0] || ""
    const lastName = nameParts.slice(1).join(" ") || ""

    // Calculer les points de fidélité (1 point par euro dépensé)
    const fidelityPoints = Math.floor(totalSpent)

    // Services favoris (pour l'instant vide, à implémenter selon vos besoins)
    const favoriteServices: string[] = []

    // Note moyenne (pour l'instant 5, à implémenter selon vos besoins)
    const averageRating = 5

    return {
      id: client.clientId,
      firstName,
      lastName,
      email: client.email,
      phone: undefined, // Non disponible dans ClientInfo
      dateOfBirth: undefined, // Non disponible dans ClientInfo
      registrationDate,
      lastVisit,
      totalSpent,
      visitCount,
      averageRating,
      type,
      favoriteServices,
      fidelityPoints,
    }
  })
}

export const useClientsData = () => {
  const { clients } = useClientsQuery()
  const { booking: reservation } = useBookingQuery()

  const [searchTerm, setSearchTerm] = useState("")
  const [typeFilter, setTypeFilter] = useState<string>("all")
  const [sortState, setSortState] = useState<SortState>({
    field: null,
    direction: null,
  })

  // Transformer les données du backend ou utiliser les données fictives
  const clientsData = useMemo(() => {
    if (
      clients &&
      Array.isArray(clients) &&
      reservation &&
      Array.isArray(reservation)
    ) {
      return transformBackendDataToClients(clients, reservation)
    }
    return []
  }, [clients, reservation])

  // Filtrage des clients
  const filteredClients = useMemo(() => {
    return clientsData.filter((client) => {
      const matchesSearch =
        client.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.phone?.includes(searchTerm)

      const matchesType = typeFilter === "all" || client.type === typeFilter

      return matchesSearch && matchesType
    })
  }, [clientsData, searchTerm, typeFilter])

  // Statistiques
  const stats = useMemo(() => {
    const total = clientsData.length
    const newClients = clientsData.filter((c) => c.type === "new").length
    const totalRevenue = clientsData.reduce((sum, c) => sum + c.totalSpent, 0)
    const averageSpent = total > 0 ? totalRevenue / total : 0
    const totalVisits = clientsData.reduce((sum, c) => sum + c.visitCount, 0)

    return { total, newClients, totalRevenue, averageSpent, totalVisits }
  }, [clientsData])

  // Tri des clients
  const sortedClients = useMemo(() => {
    if (!sortState.field || !sortState.direction) {
      return filteredClients
    }
    return sortClients(filteredClients, sortState.field, sortState.direction)
  }, [filteredClients, sortState])

  const handleSortChange = (field: SortField) => {
    setSortState((prevState) => {
      if (prevState.field === field) {
        // Même colonne : asc → desc → null
        if (prevState.direction === "asc") {
          return { field, direction: "desc" }
        } else if (prevState.direction === "desc") {
          return { field: null, direction: null }
        } else {
          return { field, direction: "asc" }
        }
      } else {
        // Nouvelle colonne : commencer par asc
        return { field, direction: "asc" }
      }
    })
  }

  return {
    // Data
    allClients: clientsData,
    filteredClients: sortedClients,
    stats,

    // Filters
    searchTerm,
    typeFilter,

    // Sorting
    sortState,

    // Actions
    setSearchTerm,
    setTypeFilter,
    handleSortChange,
  }
}
